/**
 * LinkedIn display names are not names — they are a marketing field. They carry
 * honorifics ('Dr. Marcel Walter'), post-nominals ('Jane Doe, MBA'), pronouns
 * ('Marcel Walter (He/Him)') and taglines ('Steffen Wetzel - Dipl. Kfm. | B2B').
 * Splitting such a string on the first space makes {{firstName}} render as
 * 'Dr.' and the message opens with "Hallo Dr.,".
 *
 * Mirrors backend/src/utils/personName.js and the campaign executor's copy —
 * the campaign preview must show exactly what gets sent.
 */

/** Emoji and pictographic icons: '🪶Molly Cain', 'DeanAnders⭐Field'. */
export function stripEmoji(input?: string | null): string {
  return String(input || '')
    .replace(/[\p{Extended_Pictographic}\u{1F1E6}-\u{1F1FF}\u{FE0F}\u{200D}]/gu, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

// Stripped whether or not they are written with a dot ('Dr Marcel', 'Dr. Marcel').
const STRONG_TITLES = new Set([
  'dr', 'drmed', 'dring', 'drhc', 'prof', 'professor', 'doz', 'dozent', 'privdoz',
  'dipl', 'dipling', 'diplkfm', 'diplkff', 'diploec', 'diplpsych', 'diplom', 'ing', 'mag', 'mgr', 'inz',
  'mr', 'mrs', 'ms', 'mx', 'miss', 'sir', 'dame', 'lord', 'lady', 'madam', 'madame', 'monsieur',
  'herr', 'frau', 'mevrouw', 'dhr', 'drs', 'dott', 'dottssa', 'sra', 'srta',
  'rev', 'reverend', 'pastor', 'rabbi', 'imam', 'father',
  'capt', 'captain', 'col', 'colonel', 'maj', 'sgt', 'gen', 'lt',
  'adv', 'atty', 'hon', 'eng',
]);

// Only titles when written with a dot — bare, they collide with real names
// ('Ir', 'Fr', 'Hr', 'Sr', 'Br') or with initials.
const DOTTED_TITLES = new Set([
  'ir', 'fr', 'hr', 'sr', 'br', 'st', 'lic', 'arch', 'univ', 'pd', 'priv', 'habil',
  'med', 'rer', 'nat', 'phil', 'jur', 'oec', 'pol', 'sc', 'hc', 'mult',
]);

// Post-nominals: 'Marcel Walter, MBA', 'Jane Doe PhD'.
const SUFFIXES = new Set([
  'mba', 'emba', 'phd', 'dphil', 'msc', 'bsc', 'mph', 'mpa', 'mst',
  'md', 'dds', 'dmd', 'dvm', 'jd', 'llm', 'llb', 'esq',
  'cpa', 'cfa', 'cma', 'cfp', 'pmp', 'prince2', 'cissp', 'csm', 'cscp', 'ccna', 'ccnp', 'mcse',
  'acca', 'cim', 'cipd', 'shrm', 'sphr', 'phr', 'faia', 'aia', 'frcs', 'mrcp', 'fca',
  'bcom', 'mcom', 'bba', 'btech', 'mtech',
  'jr', 'sr', 'ii', 'iii', 'iv',
]);

// ' | ', ' - ', ' • ', ' / ' … everything after the first one is a tagline.
const TAGLINE_SEPARATOR = /\s*[|•·▪★☆»«]\s*|\s+[-–—/\\@]\s+|\s+[:;]\s+/;

function normalizeToken(token: string): string {
  return String(token || '').toLowerCase().replace(/[.\-‐‑’'`]/g, '');
}

/**
 * Everything after a comma on a LinkedIn display name is credentials or a pitch
 * ('Jane Doe, MBA', 'Sarah Connor, Head of Sales at Acme') — never part of the
 * person's own name.
 */
function dropCommaTail(text: string): string {
  const head = text.split(',')[0].trim();
  return head || text;
}

function stripTitles(tokens: string[]): string[] {
  let i = 0;
  let dropped = 0;
  while (i < tokens.length) {
    const norm = normalizeToken(tokens[i]);
    const dotted = tokens[i].includes('.');
    const isTitle =
      (!!norm && STRONG_TITLES.has(norm)) ||
      (dotted && DOTTED_TITLES.has(norm)) ||
      // 'Dr. rer. nat. Marcel' — short dotted qualifiers trailing a title we already dropped.
      (dropped > 0 && dotted && norm.length > 0 && norm.length <= 5);
    if (!isTitle) break;
    i++;
    dropped++;
  }
  return tokens.slice(i);
}

function stripSuffixes(tokens: string[]): string[] {
  let end = tokens.length;
  while (end > 0 && SUFFIXES.has(normalizeToken(tokens[end - 1]))) end--;
  return tokens.slice(0, end);
}

/**
 * An email address — never a person's name, whichever field it arrived in. Mirrors the
 * executor's helper: a sender "name" that is really an address is skipped, instead of
 * being cleaned into "afajfric gmail.com" with the "@" gone.
 */
export function looksLikeEmail(value?: string | null): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

/**
 * Reduce a LinkedIn display name to the plain name.
 * Returns '' when nothing but titles/credentials is left, so callers fall back
 * to the campaign's variable fallback instead of greeting someone as 'Dr.'.
 */
export function cleanPersonName(raw?: string | null): string {
  let text = stripEmoji(raw);
  if (!text) return '';

  text = text.replace(/[(\[{<（][^)\]}>）]*[)\]}>）]/g, ' '); // '(He/Him)', '[LION]'
  // First non-empty segment: a leading '★ Anna Smith' must not split into ''.
  text = text.split(TAGLINE_SEPARATOR).map(s => s.trim()).find(Boolean) || '';
  text = dropCommaTail(text);
  text = text.replace(/[^\p{L}\p{M}\s.'’‘\-]+/gu, ' ').replace(/\s+/g, ' ').trim();
  if (!text) return '';

  const tokens = stripSuffixes(stripTitles(text.split(' ').filter(Boolean)));
  return tokens
    .join(' ')
    .replace(/[.\s]+$/, '')
    .trim();
}

/**
 * First/last name for a campaign lead: the stored fields when they survive
 * cleaning ('Dr. Marcel' → 'Marcel'), otherwise the cleaned display name split
 * on the first space ('Dr.' → '' → 'Marcel Walter' → 'Marcel' + 'Walter').
 */
export function resolveLeadNameParts(
  lead?: { firstName?: string; lastName?: string; name?: string } | null,
): { firstName: string; lastName: string } {
  const full = cleanPersonName(lead?.name);
  const fullTokens = full ? full.split(' ') : [];

  const firstName = cleanPersonName(lead?.firstName) || fullTokens[0] || '';
  const lastName = cleanPersonName(lead?.lastName) || fullTokens.slice(1).join(' ') || '';

  return { firstName, lastName };
}
